import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CloseCircle } from "iconsax-react";
import { Button, PromptForm } from "@/shared/ui";
import { useCancelSigningMutation } from "../api";
import { getCancelSigningErrorMessage } from "../utils";
import type { CancelSigningTag } from "../types";

interface Props {
  url: string;
  tags: CancelSigningTag[];
  setPrompt: (prompt: { title: string; text: string; variant: "success" | "error" }) => void;
  disabled?: boolean;
  size?: "md" | "lg" | "huge";
}

export default function CancelSigningButton({ url, tags, setPrompt, disabled = false, size = "huge" }: Props) {
  const { t } = useTranslation("Common");
  const [cancelSigning, { isLoading }] = useCancelSigningMutation();
  const [confirmOpen, setConfirmOpen] = useState(false);

  async function handleConfirm() {
    if (isLoading) return;
    try {
      await cancelSigning({ url, tags }).unwrap();
      setPrompt({
        title: t("signing.cancelSigning.successTitle"),
        text: t("signing.cancelSigning.successText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("signing.cancelSigning.errorTitle"),
        text: getCancelSigningErrorMessage(err, t),
        variant: "error",
      });
    } finally {
      setConfirmOpen(false);
    }
  }

  return (
    <>
      <Button
        variant="secondary"
        size={size}
        className="w-full"
        disabled={disabled || isLoading}
        onClick={() => setConfirmOpen(true)}>
        <CloseCircle size={16} color="currentColor" />
        {isLoading ? t("signing.cancelSigning.cancelling") : t("signing.cancelSigning.button")}
      </Button>

      {confirmOpen && (
        <PromptForm
          title={t("signing.cancelSigning.confirmTitle")}
          text={t("signing.cancelSigning.confirmText")}
          variant="warning"
          onClose={() => setConfirmOpen(false)}
          onConfirm={handleConfirm}
          isLoading={isLoading}
          confirmText={t("signing.cancelSigning.confirmButton")}
          cancelText={t("signing.cancelSigning.keepButton")}
          namespace="Common"
        />
      )}
    </>
  );
}
